import React, { useState } from 'react';
import { Poppins } from 'src/common';
import Button from 'src/components/form/Button';
import { ConfirmDeleteModal } from 'src/components/modals';
import {
  useDeleteWorkspaceMutation,
  useArchiveWorkspaceMutation,
  useUnarchiveWorkspaceMutation,
  WorkspaceQuery,
} from 'src/graphql/_generated';
import styled from 'styled-components';

const Div = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 13px;
  gap: 13px;
`;

interface DangerZoneProps {
  workspace: NonNullable<WorkspaceQuery['workspace']>;
  refetch: () => void;
}

export const DangerZone: React.FC<DangerZoneProps> = ({ workspace, refetch }) => {
  const [archiveWarningModalOpen, setArchiveWarningModalOpen] = useState(false);
  const [unarchiveWarningModalOpen, setUnarchiveWarningModalOpen] = useState(false);
  const [deleteWarningModalOpen, setDeleteWarningModalOpen] = useState(false);

  const [archiveWorkspace, { loading: isArchiving }] = useArchiveWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: refetch,
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'Workspace', id: workspace.id }),
        fields: { isArchived: () => true },
      });
    },
  });

  const [unarchiveWorkspace, { loading: isUnarchiving }] = useUnarchiveWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: refetch,
    update: (cache) => {
      cache.modify({
        id: cache.identify({ __typename: 'Workspace', id: workspace.id }),
        fields: { isArchived: () => false },
      });
    },
  });

  const [deleteWorkspace, { loading: isDeleting }] = useDeleteWorkspaceMutation({
    variables: { id: workspace.id },
    onCompleted: () => {
      window.location.href = '/home';
    },
  });

  return (
    <>
      {archiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              archiveWorkspace();
              setArchiveWarningModalOpen(false);
            }}
            onClose={() => setArchiveWarningModalOpen(false)}
            confirmText="ARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to archive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      {unarchiveWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              unarchiveWorkspace();
              setUnarchiveWarningModalOpen(false);
            }}
            onClose={() => setUnarchiveWarningModalOpen(false)}
            confirmText="UNARCHIVE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to unarchive{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      {deleteWarningModalOpen && (
        <>
          <ConfirmDeleteModal
            title={null}
            onConfirm={() => {
              deleteWorkspace();
              setDeleteWarningModalOpen(false);
            }}
            onClose={() => setDeleteWarningModalOpen(false)}
            confirmText="DELETE"
          >
            <Poppins px={18} weight={600} color="cflowerBlue">
              Are you sure you want to delete{' '}
              <Poppins px={18} weight={600} color="prussianBlue">
                {workspace.name}
              </Poppins>{' '}
              ?
            </Poppins>
          </ConfirmDeleteModal>
        </>
      )}
      <Div>
        {workspace.isArchived ? (
          <>
            {workspace.actionsOnThisWorkspace?.canUnarchive && (
              <Button
                secondary
                type="submit"
                disabled={isUnarchiving}
                $constWidth={220}
                onClick={() => setUnarchiveWarningModalOpen(true)}
              >
                UNARCHIVE
              </Button>
            )}
          </>
        ) : (
          <>
            {workspace.actionsOnThisWorkspace?.canArchive && (
              <Button
                secondary
                type="submit"
                disabled={isArchiving}
                $constWidth={220}
                onClick={() => setArchiveWarningModalOpen(true)}
              >
                ARCHIVE
              </Button>
            )}
          </>
        )}
        {(workspace.actionsOnThisWorkspace?.canArchive || workspace.actionsOnThisWorkspace?.canUnarchive) && (
          <Button
            secondary
            type="submit"
            disabled={isDeleting || !workspace.actionsOnThisWorkspace?.canDelete}
            $constWidth={220}
            onClick={() => setDeleteWarningModalOpen(true)}
          >
            DELETE
          </Button>
        )}
      </Div>
    </>
  );
};
